import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    X, Send, Sparkles, Bot, User, FileText, Brain, CheckSquare, BookOpen,
    Save, Clock, Loader, ChevronRight, ChevronLeft, Lightbulb, RefreshCw, Trash2,
    Settings, Users, Calendar, Paperclip, AlertCircle, Check, Upload, Globe, File,
    GripVertical, Edit3, ChevronDown, ChevronUp, ExternalLink, Shield, PlayCircle,
    AlignLeft, Eye, Loader2, PanelLeftClose, PanelLeftOpen
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import QuestionList from './QuestionList';

// Web Search Results Display Component
const WebSearchResultsDisplay = ({ results }) => {
    const [expandedIndex, setExpandedIndex] = useState(null);

    if (!results || results.length === 0) return null;

    return (
        <div className="mt-3 pt-3 border-t border-slate-700/30">
            <div className="flex items-center gap-2 mb-2">
                <Globe className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-medium text-slate-400">Web Sources</span>
            </div>
            <div className="space-y-2">
                {results.map((result, index) => (
                    <div
                        key={index}
                        className="bg-slate-800/40 border border-slate-700/50 rounded-lg overflow-hidden"
                    >
                        <div className="px-3 py-2">
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <a
                                        href={result.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm font-medium text-blue-400 hover:text-blue-300 truncate block"
                                    >
                                        {result.title}
                                    </a>
                                    <span className="text-xs text-slate-500">{result.domain}</span>
                                </div>
                                <a
                                    href={result.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1 text-slate-500 hover:text-blue-400 transition-colors flex-shrink-0"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                            </div>
                            <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                                {result.snippet}
                            </p>
                        </div>
                        {result.fullContent && (
                            <>
                                {expandedIndex === index && (
                                    <div className="px-3 pb-2">
                                        <div className="text-xs text-slate-300 bg-slate-900/50 p-2 rounded max-h-40 overflow-y-auto">
                                            {result.fullContent}
                                        </div>
                                    </div>
                                )}
                                <button
                                    onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                                    className="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-xs text-slate-400 hover:text-slate-300 bg-slate-800/60 hover:bg-slate-700/50 transition-colors"
                                >
                                    {expandedIndex === index ? (
                                        <>
                                            <ChevronUp className="w-3 h-3" />
                                            <span>Show Less</span>
                                        </>
                                    ) : (
                                        <>
                                            <ChevronDown className="w-3 h-3" />
                                            <span>View More</span>
                                        </>
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const AISurveyGenerator = ({ isOpen, onClose, onSuccess, editSurvey = null, prefilledData = null }) => {
    // Determine if we're in edit mode
    const isEditMode = editSurvey !== null && editSurvey?.id;

    // Chat State
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const chatEndRef = useRef(null);
    const inputRef = useRef(null);

    // Mobile State
    const [mobileActivePanel, setMobileActivePanel] = useState('chat');

    // Chat panel collapse state (desktop only)
    const [isChatCollapsed, setIsChatCollapsed] = useState(() => {
        const saved = localStorage.getItem('ai_survey_generator_chat_collapsed');
        return saved === 'true';
    });

    const toggleChatCollapsed = useCallback(() => {
        setIsChatCollapsed(prev => {
            const next = !prev;
            localStorage.setItem('ai_survey_generator_chat_collapsed', String(next));
            return next;
        });
    }, []);

    // Split Panel State
    const [splitRatio, setSplitRatio] = useState(() => {
        const saved = localStorage.getItem('ai_survey_generator_split_ratio');
        return saved ? parseFloat(saved) : 40;
    });
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef(null);
    const MIN_CHAT_WIDTH = 300;
    const MIN_EDITOR_WIDTH = 400;

    // Streaming State
    const [streamingMessage, setStreamingMessage] = useState('');
    const [streamingPhase, setStreamingPhase] = useState(null);
    const [isStreaming, setIsStreaming] = useState(false);

    // Web search state
    const [isWebSearchEnabled, setIsWebSearchEnabled] = useState(false);
    const [currentWebSearchResults, setCurrentWebSearchResults] = useState(null);
    const webSearchResultsRef = useRef(null);

    // AI File Upload State
    const [aiUploadedFiles, setAiUploadedFiles] = useState([]);
    const [isFileUploading, setIsFileUploading] = useState(false);
    const [fileUploadProgress, setFileUploadProgress] = useState(0);
    const aiFileInputRef = useRef(null);

    // Survey Editor State
    const [surveyData, setSurveyData] = useState({
        title: '',
        description: '',
        trigger_type: 'immediate',
        trigger_config: {},
        target_audience_type: 'all',
        target_audience_config: {},
        is_compliance_required: false,
        is_anonymous: false,
        questions: [],
        status: 'draft'
    });

    const [isSaving, setIsSaving] = useState(false);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [availableTeams, setAvailableTeams] = useState([]);
    const [availableEmployees, setAvailableEmployees] = useState([]);
    const [activeEditorTab, setActiveEditorTab] = useState('content');

    // Step wizard
    const editorSteps = ['content', 'settings', 'assignment'];
    const currentStepIndex = editorSteps.indexOf(activeEditorTab);
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === editorSteps.length - 1;

    // Suggestions
    const [suggestions, setSuggestions] = useState([]);

    const handleNextStep = () => {
        if (!isLastStep) {
            setActiveEditorTab(editorSteps[currentStepIndex + 1]);
        }
    };

    const handlePrevStep = () => {
        if (!isFirstStep) {
            setActiveEditorTab(editorSteps[currentStepIndex - 1]);
        }
    };

    // Track unsaved changes
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const initialStateRef = useRef(null);

    // Check if editor has content (not blank)
    const hasEditorContent = () => {
        return (
            (surveyData.title && surveyData.title.trim() !== '') ||
            (surveyData.description && surveyData.description.trim() !== '') ||
            (surveyData.questions && surveyData.questions.length > 0)
        );
    };

    // Initialize session
    useEffect(() => {
        if (isOpen) {
            const newSessionId = `survey_session_${Date.now()}`;
            setSessionId(newSessionId);

            if (isEditMode) {
                setSurveyData({
                    title: editSurvey.title || '',
                    description: editSurvey.description || '',
                    trigger_type: editSurvey.trigger_type || 'immediate',
                    trigger_config: editSurvey.trigger_config || {},
                    target_audience_type: editSurvey.target_audience_type || 'all',
                    target_audience_config: editSurvey.target_audience_config || {},
                    is_compliance_required: editSurvey.is_compliance_required || false,
                    is_anonymous: editSurvey.is_anonymous || false,
                    questions: editSurvey.questions || [],
                    status: editSurvey.status || 'draft'
                });

                setMessages([{
                    id: Date.now(),
                    role: 'assistant',
                    content: `👋 You're editing **"${editSurvey.title}"**. I can help you modify this survey. What would you like to change?\n\nYou can ask me to:\n- Add or modify questions\n- Change survey settings\n- Update target audience`,
                    timestamp: new Date()
                }]);

                setSuggestions([
                    'Add more questions',
                    'Change the survey title',
                    'Update trigger settings',
                    'Modify target audience'
                ]);
            } else {
                // Helper to build context string for the AI from prefilled data
                const buildPrefilledContextString = (data) => {
                    if (!data || Object.keys(data).length === 0) return null;
                    const parts = [];
                    if (data.title) parts.push(`Title: "${data.title}"`);
                    if (data.description) parts.push(`Description: ${data.description}`);
                    if (data.category) parts.push(`Category: ${data.category}`);
                    if (data.priority) parts.push(`Priority: ${data.priority}`);
                    return parts.length > 0 ? `Please help me create this survey:\n${parts.join('\n')}` : null;
                };

                const prefilledMessageContext = buildPrefilledContextString(prefilledData);

                const initialChatMessages = [{
                    id: 1,
                    role: 'assistant',
                    content: `👋 Hello! I'm your AI Survey Assistant. I can help you create engaging **employee surveys, feedback forms, and check-in questionnaires**.\n\nTell me what you'd like to create. For example:\n- "Create a 30-day check-in survey with 5 questions"\n- "Generate an employee engagement survey"\n- "Build a manager feedback questionnaire"`,
                    timestamp: new Date()
                }];

                if (prefilledMessageContext) {
                    initialChatMessages.push({
                        id: 2,
                        role: 'user',
                        content: prefilledMessageContext,
                        timestamp: new Date()
                    });
                    initialChatMessages.push({
                        id: 3,
                        role: 'assistant',
                        content: "I've loaded the details you requested. I can refine this survey for you, or we can just save it as is. Would you like me to start generating questions based on this?",
                        timestamp: new Date()
                    });
                }

                setMessages(initialChatMessages);

                // Set default form data using prefilledData if available
                setSurveyData(prev => ({
                    ...prev,
                    title: prefilledData?.title || '',
                    description: prefilledData?.description || ''
                }));

                setSuggestions(prefilledData ? [
                    'Generate 5 questions for this survey',
                    'Expand the description',
                    'Make it anonymous'
                ] : [
                    '30-day check-in survey',
                    'Employee engagement survey',
                    'Exit interview questions',
                    'Team feedback form'
                ]);
            }

            fetchRoles();
            fetchTeams();
            fetchEmployees();
        }
    }, [isOpen, isEditMode, editSurvey]);

    // Detect changes to form data
    useEffect(() => {
        if (isOpen && initialStateRef.current !== null) {
            const currentState = JSON.stringify({ surveyData, messages: messages.length });
            const hasChanges = currentState !== initialStateRef.current;
            setHasUnsavedChanges(hasChanges && hasEditorContent());
        }
    }, [surveyData, messages, isOpen]);

    // Set initial state reference
    useEffect(() => {
        if (isOpen && initialStateRef.current === null) {
            setTimeout(() => {
                initialStateRef.current = JSON.stringify({ surveyData, messages: messages.length });
                setHasUnsavedChanges(false);
            }, 500);
        }
    }, [isOpen]);



    // Handle browser back button - show unsaved changes modal
    useEffect(() => {
        if (!isOpen) return;

        const handlePopState = () => {
            if (isOpen && hasUnsavedChanges && hasEditorContent() && !showUnsavedModal) {
                window.history.pushState(null, '', window.location.href);
                setShowUnsavedModal(true);
            }
        };

        window.history.pushState(null, '', window.location.href);
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isOpen, hasUnsavedChanges, showUnsavedModal]);

    // Auto-scroll chat
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, streamingMessage]);

    // Focus input on open
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.top = `-${window.scrollY}px`;
        } else {
            const scrollY = document.body.style.top;
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
            window.scrollTo(0, parseInt(scrollY || '0') * -1);
        }

        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
        };
    }, [isOpen]);

    // Drag handlers for resizable split panel
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging || !containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const mouseX = e.clientX - containerRect.left;

        let newRatio = (mouseX / containerWidth) * 100;
        const minChatRatio = (MIN_CHAT_WIDTH / containerWidth) * 100;
        const maxChatRatio = 100 - (MIN_EDITOR_WIDTH / containerWidth) * 100;

        newRatio = Math.max(minChatRatio, Math.min(maxChatRatio, newRatio));
        setSplitRatio(newRatio);
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            localStorage.setItem('ai_survey_generator_split_ratio', splitRatio.toString());
        }
    }, [isDragging, splitRatio]);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        } else {
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // Fetch data
    const fetchRoles = async () => {
        try {
            const response = await api.get('/employees/roles');
            setAvailableRoles(response.data.roles || []);
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    const fetchTeams = async () => {
        try {
            const response = await api.get('/teams');
            // Teams API returns { teams: [...] }
            setAvailableTeams(response.data.teams || response.data || []);
        } catch (error) {
            console.error('Error fetching teams:', error);
            setAvailableTeams([]);
        }
    };

    const fetchEmployees = async () => {
        try {
            const response = await api.get('/employees');
            // Employees API returns { employees: [...] }
            setAvailableEmployees(response.data.employees || response.data || []);
        } catch (error) {
            console.error('Error fetching employees:', error);
            setAvailableEmployees([]);
        }
    };

    // Streaming message handler
    const handleSendMessageWithStreaming = async (messageToSend) => {
        setIsStreaming(true);
        setStreamingMessage('');
        setStreamingPhase('Analyzing your request...');

        const token = localStorage.getItem('token');
        const baseURL = api.defaults.baseURL;

        try {
            const response = await fetch(`${baseURL}/surveys/generate-ai-stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    message: messageToSend,
                    session_id: sessionId,
                    conversation_history: messages.slice(-10).map(m => ({
                        role: m.role,
                        content: m.content
                    })),
                    current_survey: {
                        title: surveyData.title,
                        description: surveyData.description,
                        questions: surveyData.questions,
                        trigger_type: surveyData.trigger_type,
                        target_audience_type: surveyData.target_audience_type,
                        is_anonymous: surveyData.is_anonymous,
                        is_compliance_required: surveyData.is_compliance_required
                    },
                    uploaded_files: aiUploadedFiles,
                    web_search_enabled: isWebSearchEnabled
                })
            });

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let accumulatedContent = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));

                            if (data.type === 'phase') {
                                setStreamingPhase(data.phase);
                            } else if (data.type === 'web_search_results') {
                                webSearchResultsRef.current = data.results;
                                setCurrentWebSearchResults(data.results);
                            } else if (data.type === 'content') {
                                accumulatedContent += data.content;

                                // Extract reply for display
                                const replyMatch = accumulatedContent.match(/"reply"\s*:\s*"((?:[^"\\]|\\.)*)(?:"|$)/);
                                if (replyMatch && replyMatch[1]) {
                                    try {
                                        const extractedReply = replyMatch[1]
                                            .replace(/\\n/g, '\n')
                                            .replace(/\\"/g, '"')
                                            .replace(/\\\\/g, '\\');
                                        setStreamingMessage(extractedReply);
                                    } catch {
                                        setStreamingMessage(replyMatch[1]);
                                    }
                                }
                            } else if (data.type === 'complete') {
                                const aiMessage = {
                                    id: Date.now() + 1,
                                    role: 'assistant',
                                    content: data.reply,
                                    timestamp: new Date(),
                                    webSearchResults: webSearchResultsRef.current || null,
                                    action: data.action,
                                    suggestedQuestions: data.survey_update?.questions || null
                                };
                                setMessages(prev => [...prev, aiMessage]);
                                setCurrentWebSearchResults(null);
                                webSearchResultsRef.current = null;

                                // Update survey data
                                if (data.survey_update) {
                                    setSurveyData(prev => ({
                                        ...prev,
                                        title: data.survey_update.title || prev.title,
                                        description: data.survey_update.description || prev.description,
                                        trigger_type: data.survey_update.trigger_type || prev.trigger_type,
                                        target_audience_type: data.survey_update.target_audience_type || prev.target_audience_type,
                                        is_anonymous: data.survey_update.is_anonymous ?? prev.is_anonymous,
                                        is_compliance_required: data.survey_update.is_compliance_required ?? prev.is_compliance_required,
                                        questions: data.survey_update.questions || prev.questions
                                    }));

                                    if (data.survey_update.title || data.survey_update.questions) {
                                        toast.success('Survey updated! Check the editor.');
                                    }
                                }

                                if (data.suggestions && data.suggestions.length > 0) {
                                    setSuggestions(data.suggestions);
                                }
                            } else if (data.type === 'error') {
                                toast.error(data.message || 'An error occurred');
                            }
                        } catch (e) {
                            // Skip invalid JSON
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Streaming error:', error);
            const errorMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: "I apologize, but I encountered an error. Please try again.",
                timestamp: new Date(),
                isError: true
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsStreaming(false);
            setStreamingMessage('');
            setStreamingPhase(null);
        }
    };

    const handleSendMessage = async () => {
        if (!inputMessage.trim() || isGenerating) return;

        const userMessage = {
            id: Date.now(),
            role: 'user',
            content: inputMessage,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        const messageToSend = inputMessage;
        setInputMessage('');
        setIsGenerating(true);

        try {
            await handleSendMessageWithStreaming(messageToSend);
        } catch (error) {
            console.error('AI chat error:', error);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setInputMessage(suggestion);
        inputRef.current?.focus();
    };

    const handleClearChat = () => {
        if (window.confirm('Clear chat history? Your survey content will be preserved.')) {
            setMessages([{
                id: Date.now(),
                role: 'assistant',
                content: "Chat cleared! Your survey content is still in the editor. How can I help you further?",
                timestamp: new Date()
            }]);
        }
    };

    const handleApplyQuestions = (questions) => {
        if (questions && questions.length > 0) {
            const formattedQuestions = questions.map((q, index) => ({
                id: q.id || `q-${Date.now()}-${index}`,
                question_text: q.question_text,
                question_type: q.question_type,
                options: q.options || null,
                is_required: q.is_required ?? true,
                order_index: surveyData.questions.length + index
            }));

            setSurveyData(prev => ({
                ...prev,
                questions: [...prev.questions, ...formattedQuestions]
            }));

            toast.success(`Added ${formattedQuestions.length} questions!`);
        }
    };

    // Save handlers
    const handleClose = () => {
        if (hasUnsavedChanges && hasEditorContent()) {
            setShowUnsavedModal(true);
        } else {
            onClose();
        }
    };

    const handleSaveAsDraftFromModal = async () => {
        setShowUnsavedModal(false);
        await handleSaveDraft();
    };

    const handleLeaveWithoutSaving = () => {
        setShowUnsavedModal(false);
        onClose();
    };

    const handleSaveDraft = async () => {
        if (!surveyData.title.trim()) {
            toast.error('Please enter a survey title');
            return;
        }

        setIsSaving(true);
        try {
            if (isEditMode) {
                await api.put(`/surveys/${editSurvey.id}`, surveyData);
                toast.success('Survey updated!');
            } else {
                await api.post('/surveys', surveyData);
                toast.success('Survey saved as draft!');
            }
            onSuccess?.();
        } catch (error) {
            console.error('Save error:', error);
            toast.error('Failed to save survey');
        } finally {
            setIsSaving(false);
        }
    };

    const handleActivate = async () => {
        if (!surveyData.title.trim()) {
            toast.error('Please enter a survey title');
            return;
        }

        if (surveyData.questions.length === 0) {
            toast.error('Add at least one question to activate');
            return;
        }

        setIsSaving(true);
        try {
            let surveyId;

            if (isEditMode) {
                await api.put(`/surveys/${editSurvey.id}`, { ...surveyData, status: 'active' });
                surveyId = editSurvey.id;
            } else {
                const createRes = await api.post('/surveys', surveyData);
                surveyId = createRes.data.id;
                await api.put(`/surveys/${surveyId}`, { status: 'active' });
            }

            const deployRes = await api.post(`/surveys/${surveyId}/deploy`);
            toast.success(`Survey Activated! ${deployRes.data.message}`);
            onSuccess?.();
            onClose?.();
        } catch (error) {
            console.error('Activate error:', error);
            toast.error('Failed to activate survey');
        } finally {
            setIsSaving(false);
        }
    };

    // Form field handlers
    const updateField = (field, value) => {
        setSurveyData(prev => ({ ...prev, [field]: value }));
    };

    const updateConfig = (field, key, value) => {
        setSurveyData(prev => ({
            ...prev,
            [field]: { ...prev[field], [key]: value }
        }));
    };

    // Direct file upload handler for inline file picker (Claude/Gemini style)
    const handleDirectFileUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Validate file count
        if (aiUploadedFiles.length + files.length > 5) {
            toast.error('Maximum 5 files allowed per session');
            if (aiFileInputRef.current) aiFileInputRef.current.value = '';
            return;
        }

        // Validate file sizes (max 10MB each)
        const invalidFiles = files.filter(file => file.size > 10 * 1024 * 1024);
        if (invalidFiles.length > 0) {
            toast.error(`Files exceed 10MB limit: ${invalidFiles.map(f => f.name).join(', ')}`);
            if (aiFileInputRef.current) aiFileInputRef.current.value = '';
            return;
        }

        setIsFileUploading(true);
        setFileUploadProgress(0);

        try {
            const formDataUpload = new FormData();
            files.forEach(file => {
                formDataUpload.append('files', file);
            });
            formDataUpload.append('session_id', sessionId);

            const response = await api.post('/files/ai-upload', formDataUpload, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setFileUploadProgress(percentCompleted);
                },
            });

            if (response.data.success) {
                const newFiles = response.data.files.filter(f => !f.error);
                setAiUploadedFiles(prev => [...prev, ...newFiles]);
                const fileNames = newFiles.map(f => f.file_name).join(', ');
                toast.success(
                    newFiles.length === 1
                        ? `Uploaded: ${fileNames}`
                        : `${newFiles.length} files uploaded`
                );
            }
        } catch (error) {
            console.error('AI file upload error:', error);
            toast.error(error.response?.data?.message || 'Failed to upload files');
        } finally {
            setIsFileUploading(false);
            setFileUploadProgress(0);
            if (aiFileInputRef.current) aiFileInputRef.current.value = '';
        }
    };

    // Remove an uploaded file
    const handleRemoveAiFile = (file) => {
        setAiUploadedFiles(prev => prev.filter(f => f.file_key !== file.file_key));
    };

    if (!isOpen) return null;

    return (
        <div ref={containerRef} className="fixed inset-0 z-50 bg-slate-950 flex flex-col">
            {/* Header - Survey Generator */}
            <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 to-blue-600 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-white">
                            {isEditMode ? 'Edit Survey with AI' : 'AI Survey Generator'}
                        </h2>
                        <p className="text-white/80 text-sm">Create surveys & forms</p>
                    </div>

                    {/* AI Chat Toggle — left side, with label */}
                    <button
                        onClick={toggleChatCollapsed}
                        className="hidden lg:flex items-center gap-2 ml-3 px-3 py-1.5 rounded-full transition-all duration-200 bg-white/15 hover:bg-white/25 border border-white/20 hover:border-white/40"
                        title={isChatCollapsed ? 'Show AI Chat Panel' : 'Hide AI Chat Panel'}
                    >
                        {isChatCollapsed ? (
                            <>
                                <Bot className="w-4 h-4 text-white" />
                                <span className="text-xs font-semibold text-white whitespace-nowrap">Show AI Chat</span>
                            </>
                        ) : (
                            <>
                                <PanelLeftClose className="w-4 h-4 text-white" />
                                <span className="text-xs font-semibold text-white/80 whitespace-nowrap">Hide Chat</span>
                            </>
                        )}
                    </button>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleClearChat}
                        className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                        title="Clear chat"
                    >
                        <RefreshCw className="w-5 h-5" />
                    </button>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-white" />
                    </button>
                </div>
            </div>

            {/* Mobile Panel Toggle */}
            <div className="lg:hidden flex bg-slate-900 border-b border-slate-800">
                <button
                    onClick={() => setMobileActivePanel('chat')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${mobileActivePanel === 'chat'
                        ? 'bg-slate-800 text-white border-b-2 border-indigo-500'
                        : 'text-slate-400 hover:text-white'
                        }`}
                >
                    <Sparkles className="w-4 h-4" />
                    AI Chat
                </button>
                <button
                    onClick={() => setMobileActivePanel('editor')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${mobileActivePanel === 'editor'
                        ? 'bg-white text-gray-900 border-b-2 border-indigo-500'
                        : 'text-slate-400 hover:text-white'
                        }`}
                >
                    <FileText className="w-4 h-4" />
                    Survey Editor
                </button>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Collapsed Chat Sidebar Strip (desktop only) */}
                {isChatCollapsed && (
                    <div
                        className="hidden lg:flex flex-col items-center bg-slate-800 border-r border-slate-700 cursor-pointer hover:bg-slate-750 transition-colors group"
                        style={{ width: '48px', minWidth: '48px' }}
                        onClick={toggleChatCollapsed}
                        title="Expand AI Chat"
                    >
                        <div className="flex flex-col items-center gap-3 py-5">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-lg shadow-indigo-500/20 group-hover:scale-110 transition-transform">
                                <Bot className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex flex-col items-center gap-1">
                                <span className="text-[10px] font-bold text-slate-400 tracking-widest uppercase" style={{ writingMode: 'vertical-lr' }}>AI Chat</span>
                            </div>
                        </div>
                        <div className="mt-auto mb-4 p-1.5 rounded-lg bg-slate-700/50 group-hover:bg-indigo-600/30 transition-colors">
                            <PanelLeftOpen className="w-4 h-4 text-slate-400 group-hover:text-indigo-300 transition-colors" />
                        </div>
                    </div>
                )}

                {/* Chat Panel */}
                <div
                    className={`flex flex-col bg-slate-800 ${mobileActivePanel === 'chat' ? 'flex' : 'hidden lg:flex'}`}
                    style={{
                        width: window.innerWidth >= 1024
                            ? (isChatCollapsed ? '0px' : `${splitRatio}%`)
                            : '100%',
                        minWidth: window.innerWidth >= 1024 && isChatCollapsed ? '0px' : undefined,
                        overflow: isChatCollapsed && window.innerWidth >= 1024 ? 'hidden' : undefined,
                        opacity: isChatCollapsed && window.innerWidth >= 1024 ? 0 : 1,
                        transition: 'width 300ms ease, opacity 200ms ease, min-width 300ms ease',
                        pointerEvents: isChatCollapsed && window.innerWidth >= 1024 ? 'none' : undefined
                    }}
                >
                    <div className="flex justify-between items-center px-4 py-2 border-b border-slate-700/50 lg:hidden">
                        <span className="text-xs font-semibold text-slate-400">AI Chat</span>
                    </div>

                    {/* Chat Messages */}
                    < div className="flex-1 overflow-y-auto overscroll-contain px-5 py-6 space-y-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {
                            messages.map((message) => (
                                <div
                                    key={message.id}
                                    className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : ''}`}
                                >
                                    {message.role === 'user' ? (
                                        <div className="max-w-[85%]">
                                            <div className="bg-violet-600 rounded-2xl rounded-tr-none px-4 py-3 shadow-lg shadow-violet-900/20">
                                                <p className="text-sm text-white whitespace-pre-wrap">
                                                    {message.content}
                                                </p>
                                            </div>
                                            <p className="text-[11px] text-slate-500 mt-1.5 text-right">
                                                {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="bg-gradient-to-br from-violet-500 to-purple-600 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center">
                                                <Bot className="w-4 h-4 text-white" />
                                            </div>
                                            <div className="max-w-[85%] space-y-3 p-4 bg-slate-700 rounded-2xl rounded-tl-none">
                                                <div className="space-y-3">
                                                    {/* Action Cards */}
                                                    {message.action && ['generated', 'updated'].includes(message.action) && (
                                                        <div className="flex items-center gap-2">
                                                            <FileText className="w-4 h-4 text-slate-400" />
                                                            <span className="text-sm text-slate-400">{message.action === 'generated' ? 'Created' : 'Updated'}</span>
                                                            <span className="text-sm text-slate-200 bg-slate-800 px-2.5 py-0.5 rounded border border-slate-600">
                                                                Survey
                                                            </span>
                                                        </div>
                                                    )}

                                                    {/* Message Content */}
                                                    <div className="text-[15px] text-slate-200 leading-relaxed">
                                                        {message.content.split('\n').map((line, i) => (
                                                            <p key={i} className="mb-1.5 last:mb-0">
                                                                {line.startsWith('**') && line.endsWith('**') ? (
                                                                    <strong className="text-white">{line.slice(2, -2)}</strong>
                                                                ) : line.startsWith('- ') ? (
                                                                    <span className="flex gap-2 text-slate-300">
                                                                        <span className="text-indigo-400">•</span>
                                                                        {line.slice(2)}
                                                                    </span>
                                                                ) : line}
                                                            </p>
                                                        ))}
                                                    </div>

                                                    {/* Web Search Results */}
                                                    {message.webSearchResults && message.webSearchResults.length > 0 && (
                                                        <WebSearchResultsDisplay results={message.webSearchResults} />
                                                    )}

                                                    {/* Suggested Questions Card */}
                                                    {message.suggestedQuestions && message.suggestedQuestions.length > 0 && (
                                                        <div className="mt-3 bg-slate-800 rounded-xl border border-slate-600 overflow-hidden">
                                                            <div className="px-3 py-2 border-b border-slate-600 bg-slate-900/50 flex justify-between items-center">
                                                                <span className="text-xs font-bold text-indigo-400 uppercase">Generated Questions</span>
                                                                <span className="text-xs text-slate-500">{message.suggestedQuestions.length} items</span>
                                                            </div>
                                                            <ul className="divide-y divide-slate-700/50 max-h-60 overflow-y-auto bg-slate-800/50">
                                                                {message.suggestedQuestions.map((q, i) => (
                                                                    <li key={i} className="px-3 py-2 text-xs text-slate-300 flex items-start gap-2 hover:bg-slate-700/50 transition-colors">
                                                                        <div className="bg-slate-900/50 border border-slate-600 rounded px-1.5 py-0.5 text-[10px] font-mono text-indigo-400 flex-shrink-0">
                                                                            {q.question_type}
                                                                        </div>
                                                                        <span className="flex-1">{q.question_text}</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                            <div className="p-2 bg-slate-900/50 border-t border-slate-600">
                                                                <button
                                                                    onClick={() => handleApplyQuestions(message.suggestedQuestions)}
                                                                    className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium py-2 rounded-lg transition-colors shadow-sm"
                                                                >
                                                                    <Check size={14} />
                                                                    Apply to Survey
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <p className="text-[11px] text-slate-500 mt-2">
                                                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </p>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))
                        }

                        {/* Streaming Indicator */}
                        {
                            (isGenerating || isStreaming) && (
                                <div className="flex gap-3">
                                    <div className="bg-gradient-to-br from-violet-500 to-purple-600 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center">
                                        <Bot className="w-4 h-4 text-white" />
                                    </div>
                                    <div className="max-w-[85%] rounded-2xl px-4 py-3 bg-slate-700 text-slate-100">
                                        {streamingPhase && (
                                            <div className="flex items-center gap-2 text-xs text-violet-400 mb-2">
                                                <Loader2 className="w-3 h-3 animate-spin" />
                                                {streamingPhase}
                                            </div>
                                        )}
                                        {streamingMessage && (
                                            <div className="text-[15px] text-slate-200 leading-relaxed whitespace-pre-wrap">
                                                {streamingMessage}
                                                <span className="inline-block w-2 h-4 bg-violet-400 animate-pulse ml-0.5"></span>
                                            </div>
                                        )}
                                        {!streamingMessage && !streamingPhase && (
                                            <div className="flex gap-1">
                                                <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                                <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                                <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        }

                        <div ref={chatEndRef} />
                    </div >

                    {/* Suggestions */}
                    {
                        suggestions.length > 0 && !isGenerating && (
                            <div className="px-4 pb-2">
                                <div className="flex items-center gap-2 mb-2">
                                    <Lightbulb className="w-4 h-4 text-amber-400" />
                                    <span className="text-xs text-slate-400">Suggestions</span>
                                </div>
                                <div className="flex gap-2 overflow-x-auto pb-2">
                                    {suggestions.map((suggestion, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleSuggestionClick(suggestion)}
                                            className="flex-shrink-0 px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-full transition-colors whitespace-nowrap"
                                        >
                                            {suggestion}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )
                    }

                    {/* Chat Input */}
                    <div className="p-4 border-t border-slate-700">
                        {/* Hidden File Input for Claude/Gemini-style inline file picker */}
                        <input
                            ref={aiFileInputRef}
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                            onChange={handleDirectFileUpload}
                            disabled={isGenerating || isFileUploading || aiUploadedFiles.length >= 5}
                            className="hidden"
                        />

                        {/* Inline File Upload Progress */}
                        {isFileUploading && (
                            <div className="mb-3 bg-slate-800/50 rounded-lg p-2.5">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <Loader className="w-3.5 h-3.5 animate-spin text-indigo-400" />
                                    <span className="text-xs text-slate-300">Uploading...</span>
                                    <span className="text-xs text-slate-500 ml-auto">{fileUploadProgress}%</span>
                                </div>
                                <div className="w-full bg-slate-700 rounded-full h-1 overflow-hidden">
                                    <div
                                        className="bg-gradient-to-r from-indigo-500 to-violet-500 h-full rounded-full transition-all duration-300"
                                        style={{ width: `${fileUploadProgress}%` }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Uploaded Files Display - Claude/Gemini style compact chips */}
                        {aiUploadedFiles.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                                {aiUploadedFiles.map((file) => {
                                    const ext = file.file_name?.split('.').pop()?.toUpperCase() || 'FILE';
                                    const isPdf = ext === 'PDF';
                                    const isDoc = ['DOC', 'DOCX'].includes(ext);
                                    const isImage = ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP'].includes(ext);
                                    const isSpreadsheet = ['XLS', 'XLSX'].includes(ext);

                                    return (
                                        <div
                                            key={file.file_key}
                                            className="group flex items-center gap-1.5 bg-slate-800/60 hover:bg-slate-800/80 border border-slate-700/50 rounded-md px-2 py-1 transition-colors"
                                        >
                                            <div className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold ${isPdf ? 'bg-red-500/20 text-red-400' :
                                                isDoc ? 'bg-blue-500/20 text-blue-400' :
                                                    isImage ? 'bg-green-500/20 text-green-400' :
                                                        isSpreadsheet ? 'bg-emerald-500/20 text-emerald-400' :
                                                            'bg-slate-600/50 text-slate-400'
                                                }`}>
                                                {isPdf ? 'PDF' : isDoc ? 'DOC' : isImage ? 'IMG' : isSpreadsheet ? 'XLS' : ext.slice(0, 3)}
                                            </div>
                                            <span className="text-xs text-slate-300 max-w-[120px] truncate">
                                                {file.file_name}
                                            </span>
                                            <button
                                                onClick={() => handleRemoveAiFile(file)}
                                                className="p-0.5 text-slate-500 hover:text-red-400 rounded transition-colors opacity-60 group-hover:opacity-100"
                                                title="Remove file"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Input Wrapper */}
                        <div className="relative">
                            <textarea
                                ref={inputRef}
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Describe the survey you want to create..."
                                rows={2}
                                className="w-full px-4 py-3 pr-24 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                            />

                            <div className="absolute right-2 bottom-2 flex gap-1">
                                <button
                                    onClick={() => aiFileInputRef.current?.click()}
                                    disabled={isGenerating || isFileUploading || aiUploadedFiles.length >= 5}
                                    className={`p-2 rounded-lg transition-colors ${aiUploadedFiles.length > 0
                                        ? 'bg-violet-600 text-white'
                                        : 'text-slate-400 hover:text-white hover:bg-slate-600'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    title="Attach files"
                                >
                                    <Paperclip className="w-4 h-4" />
                                </button>

                                <button
                                    onClick={handleSendMessage}
                                    disabled={!inputMessage.trim() || isGenerating}
                                    className="p-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-violet-500/20"
                                >
                                    {isGenerating || isStreaming ? (
                                        <Loader className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Send className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center justify-between mt-2">
                            {/* <div className="flex items-center gap-1">
                            <button
                                onClick={() => setIsWebSearchEnabled(!isWebSearchEnabled)}
                                className={`p-2 rounded-lg transition-all ${isWebSearchEnabled
                                    ? 'text-blue-400 bg-blue-500/10'
                                    : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'
                                    }`}
                                title={isWebSearchEnabled ? "Web Search Active" : "Enable Web Search"}
                            >
                                <Globe className="w-4 h-4" />
                            </button>
                        </div> */}
                            {/* File count indicator */}
                            {aiUploadedFiles.length > 0 && (
                                <span className="text-xs text-slate-500">
                                    {aiUploadedFiles.length}/5 files attached
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Draggable Divider */}
                {
                    !isChatCollapsed && (
                        <div
                            className="hidden lg:flex flex-col items-center justify-center w-1 bg-slate-800 hover:bg-indigo-500/50 cursor-col-resize transition-colors group"
                            onMouseDown={handleMouseDown}
                        >
                            <div className="absolute flex flex-col items-center justify-center w-4 h-12 rounded bg-slate-700 group-hover:bg-indigo-500/30 transition-colors">
                                <GripVertical className="w-3 h-3 text-slate-500 group-hover:text-indigo-400" />
                            </div>
                        </div>
                    )
                }

                {/* Editor Panel */}
                <div
                    className={`flex-1 flex flex-col bg-white overflow-hidden ${mobileActivePanel === 'editor' ? 'flex' : 'hidden lg:flex'} relative transition-all duration-300`}
                    style={{
                        width: window.innerWidth >= 1024
                            ? (isChatCollapsed ? '100%' : `${100 - splitRatio}%`)
                            : '100%',
                        flex: isChatCollapsed && window.innerWidth >= 1024 ? '1 1 0%' : undefined,
                        transition: 'width 300ms ease, flex 300ms ease'
                    }}
                >
                    {/* AI Loading Overlay */}
                    {isGenerating && (
                        <div className="absolute inset-0 z-20 bg-white/50 backdrop-blur-[1px] flex flex-col items-center justify-center animate-in fade-in duration-300">
                            <div className="bg-white p-6 rounded-2xl shadow-xl border border-violet-100 flex flex-col items-center gap-4 max-w-sm mx-4 text-center">
                                <div className="relative">
                                    <div className="absolute inset-0 bg-violet-500/20 rounded-full blur-xl animate-pulse" />
                                    <div className="relative bg-gradient-to-tr from-violet-600 to-purple-600 p-4 rounded-full shadow-lg">
                                        <Loader2 className="w-8 h-8 text-white animate-spin" />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <h3 className="font-semibold text-gray-900">Generating Survey...</h3>
                                    <p className="text-sm text-gray-500">AI is crafting your questions and settings.</p>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Editor Header */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50 gap-3 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-indigo-100 text-indigo-600">
                                <FileText className="w-5 h-5" />
                            </div>
                            <div>
                                <h2 className="font-semibold text-gray-800">
                                    {surveyData.title || 'Untitled Survey'}
                                </h2>
                                <p className="text-xs text-gray-500">
                                    {surveyData.questions.length} questions • {surveyData.status}
                                </p>
                            </div>
                        </div>

                        {/* Step Progress */}
                        <div className="flex items-center gap-1 sm:gap-2 w-full sm:w-auto overflow-x-auto">
                            {[
                                { id: 'content', label: 'Content', icon: AlignLeft, step: 1 },
                                { id: 'settings', label: 'Settings', icon: Settings, step: 2 },
                                { id: 'assignment', label: 'Assignment', icon: Users, step: 3 }
                            ].map(({ id, label, icon: Icon, step }, index) => {
                                const stepIndex = editorSteps.indexOf(id);
                                const isCompleted = stepIndex < currentStepIndex;
                                const isCurrent = id === activeEditorTab;

                                return (
                                    <React.Fragment key={id}>
                                        <div className="flex items-center gap-1.5 sm:gap-2">
                                            <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold transition-all ${isCompleted
                                                ? 'bg-green-500 text-white'
                                                : isCurrent
                                                    ? 'bg-indigo-600 text-white ring-4 ring-indigo-100'
                                                    : 'bg-gray-200 text-gray-500'
                                                }`}>
                                                {isCompleted ? <Check className="w-4 h-4" /> : step}
                                            </div>
                                            <span className={`hidden sm:inline text-sm font-medium ${isCurrent ? 'text-indigo-600' : isCompleted ? 'text-green-600' : 'text-gray-400'
                                                }`}>
                                                {label}
                                            </span>
                                        </div>
                                        {index < 2 && (
                                            <div className={`w-6 sm:w-10 h-0.5 ${isCompleted ? 'bg-green-500' : 'bg-gray-200'
                                                }`} />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>

                    {/* Editor Content */}
                    <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {activeEditorTab === 'content' && (
                            <div className="space-y-6">
                                {/* Title */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Survey Title *
                                    </label>
                                    <input
                                        type="text"
                                        value={surveyData.title}
                                        onChange={(e) => updateField('title', e.target.value)}
                                        placeholder="e.g., 30-Day Check-in Survey"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg"
                                    />
                                </div>

                                {/* Description */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Description
                                    </label>
                                    <textarea
                                        value={surveyData.description}
                                        onChange={(e) => updateField('description', e.target.value)}
                                        placeholder="Briefly describe the purpose of this survey..."
                                        rows="3"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    />
                                </div>

                                {/* Questions */}
                                <div>
                                    <div className="flex items-center justify-between mb-3">
                                        <label className="block text-sm font-semibold text-gray-700">
                                            Questions
                                        </label>
                                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                            {surveyData.questions.length} questions
                                        </span>
                                    </div>
                                    <QuestionList
                                        questions={surveyData.questions}
                                        setQuestions={(newQuestions) => updateField('questions', newQuestions)}
                                    />
                                </div>
                            </div>
                        )}

                        {activeEditorTab === 'settings' && (
                            <div className="space-y-6">
                                {/* Trigger Type */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Trigger Type
                                    </label>
                                    <select
                                        value={surveyData.trigger_type}
                                        onChange={(e) => updateField('trigger_type', e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    >
                                        <option value="immediate">Immediate (One-off)</option>
                                        <option value="lifecycle">Lifecycle (Days after start)</option>
                                        <option value="recurring">Recurring (Weekly/Monthly)</option>
                                        <option value="specific_date">Specific Date/Time</option>
                                    </select>
                                </div>

                                {/* Lifecycle Days */}
                                {surveyData.trigger_type === 'lifecycle' && (
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Days after start date
                                        </label>
                                        <input
                                            type="number"
                                            value={surveyData.trigger_config?.days_after_start || 30}
                                            onChange={(e) => updateConfig('trigger_config', 'days_after_start', parseInt(e.target.value))}
                                            className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        />
                                    </div>
                                )}

                                {/* Recurring Interval */}
                                {surveyData.trigger_type === 'recurring' && (
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Interval
                                        </label>
                                        <select
                                            value={surveyData.trigger_config?.interval || 'monthly'}
                                            onChange={(e) => updateConfig('trigger_config', 'interval', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        >
                                            <option value="weekly">Weekly</option>
                                            <option value="monthly">Monthly</option>
                                            <option value="quarterly">Quarterly</option>
                                        </select>
                                    </div>
                                )}

                                {/* Toggle Settings */}
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                                        <div>
                                            <span className="text-sm font-medium text-gray-800">Anonymous Responses</span>
                                            <p className="text-xs text-gray-500">Employee identities won't be recorded</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="sr-only peer"
                                                checked={surveyData.is_anonymous}
                                                onChange={(e) => updateField('is_anonymous', e.target.checked)}
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                        </label>
                                    </div>

                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                                        <div>
                                            <span className="text-sm font-medium text-gray-800">Compliance Required</span>
                                            <p className="text-xs text-gray-500">Forces visibility, skips consent step</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="sr-only peer"
                                                checked={surveyData.is_compliance_required}
                                                onChange={(e) => updateField('is_compliance_required', e.target.checked)}
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeEditorTab === 'assignment' && (
                            <div className="space-y-6">
                                {/* Target Audience Type */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Target Audience
                                    </label>
                                    <select
                                        value={surveyData.target_audience_type}
                                        onChange={(e) => updateField('target_audience_type', e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    >
                                        <option value="all">Company-Wide (All Employees)</option>
                                        <option value="role">Specific Roles</option>
                                        <option value="team">Specific Teams</option>
                                        <option value="specific_employees">Specific Employees</option>
                                    </select>
                                </div>

                                {/* Role Selection */}
                                {surveyData.target_audience_type === 'role' && (
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Select Roles
                                        </label>
                                        {availableRoles.length > 0 ? (
                                            <div className="grid grid-cols-2 gap-2">
                                                {availableRoles.map((role) => {
                                                    const roleId = typeof role === 'object' ? role.id : role;
                                                    const roleName = typeof role === 'object' ? role.role_name : role;
                                                    const isSelected = (surveyData.target_audience_config?.role_ids || []).includes(roleId);
                                                    return (
                                                        <button
                                                            key={roleId}
                                                            type="button"
                                                            onClick={() => {
                                                                const currentRoleIds = surveyData.target_audience_config?.role_ids || [];
                                                                const newRoleIds = isSelected
                                                                    ? currentRoleIds.filter(r => r !== roleId)
                                                                    : [...currentRoleIds, roleId];
                                                                updateConfig('target_audience_config', 'role_ids', newRoleIds);
                                                            }}
                                                            className={`p-3 border-2 rounded-lg text-left transition-all ${isSelected
                                                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                                                : 'border-gray-200 hover:border-gray-300'
                                                                }`}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-indigo-500 border-indigo-500' : 'border-gray-300'}`}>
                                                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                                                </div>
                                                                <span className="text-sm font-medium">{roleName}</span>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-500">No roles available</p>
                                        )}
                                    </div>
                                )}

                                {/* Team Selection */}
                                {surveyData.target_audience_type === 'team' && (
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Select Teams
                                        </label>
                                        {availableTeams.length > 0 ? (
                                            <div className="grid grid-cols-2 gap-2">
                                                {availableTeams.map((team) => {
                                                    const isSelected = (surveyData.target_audience_config?.team_ids || []).includes(team.id);
                                                    return (
                                                        <button
                                                            key={team.id}
                                                            type="button"
                                                            onClick={() => {
                                                                const currentTeamIds = surveyData.target_audience_config?.team_ids || [];
                                                                const newTeamIds = isSelected
                                                                    ? currentTeamIds.filter(t => t !== team.id)
                                                                    : [...currentTeamIds, team.id];
                                                                updateConfig('target_audience_config', 'team_ids', newTeamIds);
                                                            }}
                                                            className={`p-3 border-2 rounded-lg text-left transition-all ${isSelected
                                                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                                                : 'border-gray-200 hover:border-gray-300'
                                                                }`}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-indigo-500 border-indigo-500' : 'border-gray-300'}`}>
                                                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                                                </div>
                                                                <span className="text-sm font-medium">{team.team_name || team.name}</span>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-500">No teams available</p>
                                        )}
                                    </div>
                                )}

                                {/* Specific Employees Selection */}
                                {surveyData.target_audience_type === 'specific_employees' && (
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Select Employees
                                        </label>
                                        {availableEmployees.length > 0 ? (
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                        {(surveyData.target_audience_config?.employee_ids || []).length} selected
                                                    </span>
                                                </div>
                                                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                                                    {availableEmployees.map((employee) => {
                                                        const isSelected = (surveyData.target_audience_config?.employee_ids || []).includes(employee.id);
                                                        return (
                                                            <button
                                                                key={employee.id}
                                                                type="button"
                                                                onClick={() => {
                                                                    const currentEmployeeIds = surveyData.target_audience_config?.employee_ids || [];
                                                                    const newEmployeeIds = isSelected
                                                                        ? currentEmployeeIds.filter(e => e !== employee.id)
                                                                        : [...currentEmployeeIds, employee.id];
                                                                    updateConfig('target_audience_config', 'employee_ids', newEmployeeIds);
                                                                }}
                                                                className={`w-full p-3 text-left transition-all flex items-center gap-3 ${isSelected
                                                                    ? 'bg-indigo-50'
                                                                    : 'hover:bg-gray-50'
                                                                    }`}
                                                            >
                                                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-indigo-500 border-indigo-500' : 'border-gray-300'}`}>
                                                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-sm font-medium text-gray-800 truncate">
                                                                        {employee.first_name} {employee.last_name}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500 truncate">
                                                                        {employee.email} {employee.position && `• ${employee.position}`}
                                                                    </p>
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-500">No employees available</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Editor Footer */}
                    <div className="border-t border-gray-200 bg-gray-50 px-4 sm:px-6 py-3 sm:py-4">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                            {/* Left side - Status */}
                            <div className="flex items-center gap-2 text-sm text-gray-500 justify-center sm:justify-start">
                                {isLastStep ? (
                                    surveyData.title && surveyData.questions.length > 0 ? (
                                        <>
                                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                            Ready to activate
                                        </>
                                    ) : (
                                        <>
                                            <AlertCircle className="w-4 h-4 text-amber-500" />
                                            {!surveyData.title ? 'Add a title' : 'Add questions'} to activate
                                        </>
                                    )
                                ) : (
                                    <span className="text-gray-400">Step {currentStepIndex + 1} of {editorSteps.length}</span>
                                )}
                            </div>

                            {/* Right side - Navigation & Actions */}
                            <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2 sm:gap-3">
                                <button
                                    onClick={handleClose}
                                    className="px-3 sm:px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                                >
                                    Cancel
                                </button>

                                {!isFirstStep && (
                                    <button
                                        onClick={handlePrevStep}
                                        className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                        Back
                                    </button>
                                )}

                                <button
                                    onClick={handleSaveDraft}
                                    disabled={isSaving || !surveyData.title.trim()}
                                    className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 text-sm"
                                >
                                    <Save className="w-4 h-4" />
                                    <span className="hidden xs:inline">Save </span>Draft
                                </button>

                                {!isLastStep && (
                                    <button
                                        onClick={handleNextStep}
                                        className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm"
                                    >
                                        Next
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                )}

                                {isLastStep && (
                                    <button
                                        onClick={handleActivate}
                                        disabled={isSaving || !surveyData.title.trim() || surveyData.questions.length === 0}
                                        className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white rounded-lg transition-colors disabled:opacity-50 shadow-lg shadow-indigo-500/25 text-sm"
                                    >
                                        <PlayCircle className="w-4 h-4" />
                                        Review & Activate
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

            </div>


            {/* Unsaved Changes Modal */}
            {
                showUnsavedModal && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-in fade-in duration-200">
                        <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full mx-auto overflow-hidden ring-1 ring-black/5 transform transition-all scale-100 opacity-100">
                            {/* Header */}
                            <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-8 py-6 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-xl"></div>
                                <div className="relative flex items-center gap-4">
                                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm shadow-inner">
                                        <AlertCircle className="w-7 h-7 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold text-white tracking-tight">Unsaved Changes</h3>
                                        <p className="text-amber-100 font-medium text-sm">You have unsaved work in progress</p>
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="px-8 py-8">
                                <p className="text-gray-600 text-lg leading-relaxed mb-6">
                                    You have modified this survey but haven't saved your changes yet. If you leave now, your progress will be lost.
                                </p>

                                {/* Survey Preview */}
                                {surveyData.title && (
                                    <div className="p-5 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 shadow-sm relative overflow-hidden group hover:border-violet-200 transition-colors">
                                        <div className="absolute top-0 right-0 w-24 h-24 bg-gray-100 rounded-full -mr-12 -mt-12 opacity-50"></div>
                                        <div className="flex items-start gap-4 relative">
                                            <div className="p-3 bg-indigo-50 rounded-xl text-indigo-600 shadow-sm group-hover:scale-110 transition-transform duration-300">
                                                <FileText className="w-6 h-6" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-bold text-gray-900 truncate text-lg pr-4">{surveyData.title}</h4>
                                                <div className="flex items-center gap-3 mt-1.5">
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold uppercase tracking-wide">
                                                        Survey
                                                    </span>
                                                    <span className="text-sm text-gray-500 font-medium">
                                                        {surveyData.questions.length} question{surveyData.questions.length !== 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {!surveyData.title && (
                                    <div className="p-4 bg-amber-50 text-amber-800 rounded-xl border border-amber-100 text-sm font-medium flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4" />
                                        Please add a title to save this survey as a draft.
                                    </div>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="px-8 py-6 bg-gray-50/80 border-t border-gray-100 flex flex-col-reverse sm:flex-row gap-4">
                                <button
                                    onClick={handleLeaveWithoutSaving}
                                    className="flex-1 px-6 py-3.5 text-gray-600 bg-white hover:bg-red-50 hover:text-red-600 hover:border-red-200 border border-gray-200 rounded-xl font-semibold transition-all duration-200 shadow-sm focus:ring-2 focus:ring-red-100 outline-none"
                                >
                                    Leave Without Saving
                                </button>
                                <button
                                    onClick={handleSaveAsDraftFromModal}
                                    disabled={isSaving || !surveyData.title?.trim()}
                                    className="flex-[1.5] px-6 py-3.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg shadow-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 transform hover:-translate-y-0.5 focus:ring-2 focus:ring-violet-200 outline-none"
                                >
                                    {isSaving ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            Saving Draft...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-5 h-5" />
                                            Save Changes & Leave
                                        </>
                                    )}
                                </button>
                            </div>

                            {!surveyData.title?.trim() && (
                                <div className="px-6 pb-4">
                                    <p className="text-xs text-amber-600 flex items-center gap-1.5">
                                        <AlertCircle className="w-3.5 h-3.5" />
                                        Survey title is required to save as draft
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default AISurveyGenerator;
